import type { ParseKeys } from "i18next";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components";
import { TokenDetailsView } from "../../components";
import { Transaction } from "../../../../../model/transaction";
import { formatDate, formatTime } from "../../history.utils";
import styles from "../customer-transaction-history-detail.module.scss";
import { formatCurrency, handleCopy } from "../../../../../utils";
import { CopyIcon } from "../../../../../assets/icons";
import { VendingStepType, VendingStepStatus } from "../../../components/transaction-result-v2/types";

const VENDING_STEP_NAMES: Record<VendingStepType, ParseKeys> = {
	[VendingStepType.UNKNOWN]: "common.labels.unknown",
	[VendingStepType.PAYMENT]: "common.labels.payment",
	[VendingStepType.TOKEN_GENERATION]: "common.labels.tokenGeneration",
	[VendingStepType.SMS_EMAIL_DELIVERY]: "common.labels.smsEmailDelivery",
	[VendingStepType.REMOTE_LOAD]: "common.labels.remoteLoad",
};

interface VendingTransactionDetailProps {
	transaction: Transaction;
	showTokenView: boolean;
	onHideTokenView: () => void;
	onDownloadReceipt: () => void;
	isDownloading: boolean;
	shareView: boolean;
}

export const VendingTransactionDetail = ({
	transaction,
	showTokenView,
	onHideTokenView,
	onDownloadReceipt,
	isDownloading,
	shareView,
}: VendingTransactionDetailProps) => {
	const { t } = useTranslation();
	// const navigate = useNavigate();
	// const currentScreen: CurrentScreen = useWindowDimensions();

	const hasVendingToken = !!transaction.vendingDetails?.vendingToken?.token;
	const tokenValue = transaction.vendingDetails?.vendingToken?.token || "";
	const vending = transaction.vendingDetails;
	// const statusKey = getTransactionStatusForDisplay(transaction.status);

	if (showTokenView && hasVendingToken) {
		return (
			<TokenDetailsView
				token={tokenValue}
				onClose={onHideTokenView}
				onDownloadReceipt={onDownloadReceipt}
				onViewTransaction={onHideTokenView}
				isDownloading={isDownloading}
			/>
		);
	}

	return (
		<>
			{/* token */}

			<div className={styles.tokenSection}>
				<span className={styles.receiptLabel}>{t("history.yourToken")}</span>
				{transaction.vendingDetails?.vendingToken?.token ? (
					<div className={styles.tokenCopyContainer}>
						<span className={styles.tokenValue}>{transaction.vendingDetails.vendingToken.token}</span>
						<div className={styles.containerDel}>
							<hr className={styles.delimiter} />
							<Button variant="outline" onClick={() => handleCopy(transaction.vendingDetails?.vendingToken?.token ?? "")}>
								{t("common.actions.copy")} <CopyIcon className={styles.copyBtn} />
							</Button>
						</div>
					</div>
				) : (
					<span className={styles.tokenValue}>{t("history.web.notAvailableToken")}</span>
				)}
			</div>
			{/* MAP Token */}
			{!!transaction.vendingDetails?.vendingToken?.mapRefundToken && (
				<div className={styles.tokenSection}>
					<span className={styles.receiptLabel}>{t("history.mapToken")}</span>
					<div className={styles.tokenCopyContainer}>
						<span className={styles.tokenValue}>{transaction.vendingDetails.vendingToken.mapRefundToken}</span>
						<div className={styles.containerDel}>
							<hr className={styles.delimiter} />
							<Button variant="outline" onClick={() => handleCopy(transaction.vendingDetails?.vendingToken?.mapRefundToken ?? "")}>
								{t("common.actions.copy")} <CopyIcon className={styles.copyBtn} />
							</Button>
						</div>
					</div>
				</div>
			)}

			{/* history steps */}
			{!shareView && (!vending?.historySteps || vending.historySteps.length === 0) && (
				<div className={styles.detailsCard}>
					<span className={styles.receiptLabel}>{t("history.web.transactionSteps")}</span>
					<span className={styles.receiptValue} style={{ textAlign: "left" }}>
						{t("history.web.noTransactionSteps")}
					</span>
				</div>
			)}
			{!shareView && vending?.historySteps && vending.historySteps.length > 0 && (
				<div className={styles.historySteps}>
					<span className={styles.receiptLabel} style={{ marginBottom: "8px" }}>
						{t("history.web.transactionSteps")}
					</span>
					{vending.historySteps.map((step, index) => {
						const isFailed = step.subTypeOfEvent === VendingStepStatus.FAILED;
						const isSuccess = step.subTypeOfEvent === VendingStepStatus.SUCCESSFUL;
						const stepNameKey = VENDING_STEP_NAMES[step.typeOfEvent as VendingStepType];
						const stepName = stepNameKey ? t(stepNameKey) : step.vendingStepName;
						return (
							<div key={index} className={styles.historyStep}>
								<div className={styles.historyStepLeft}>
									<div
										className={`${styles.historyStepIcon} ${isFailed ? styles.historyStepFailed : isSuccess ? styles.historyStepSuccess : styles.historyStepPending}`}
									>
										{isFailed ? "✕" : "✓"}
									</div>
									{index < vending.historySteps!.length - 1 && <div className={styles.historyStepLine} />}
								</div>
								<div className={styles.historyStepContent}>
									<div className={styles.historyStepRow}>
										<span className={styles.historyStepName}>{stepName}</span>
										{isFailed ? (
											<span className={styles.historyStepTime}>{t("common.labels.failed")}</span>
										) : step.timestamp ? (
											<span className={styles.historyStepTime}>
												{formatDate(step.timestamp)}, {formatTime(step.timestamp).toLocaleLowerCase()}
											</span>
										) : null}
									</div>
									{/* {isFailed && (
                    <span className={styles.historyStepError}>Token couldn't be loaded automatically. Please load it manually.</span>
                  )} */}
								</div>
							</div>
						);
					})}
				</div>
			)}

			{/* Detail rows */}
			{shareView && (
				<div className={styles.detailsCard}>
					{vending?.utility && (
						<div className={styles.detailRow}>
							<span className={styles.receiptLabel}>{t("history.distributionCompany")}</span>
							<span className={styles.receiptValue}>{vending.utility.name ?? t("history.web.notApplicable")}</span>
						</div>
					)}
					{vending && (
						<>
							<div className={styles.detailRow}>
								<span className={styles.receiptLabel}>{t("common.labels.meterNumber")}</span>
								<span className={styles.receiptValue}>{vending.meterNumber || t("history.web.notApplicable")}</span>
							</div>
							<div className={styles.detailRow}>
								<span className={styles.receiptLabel}>{t("common.labels.customerName")}</span>
								<span className={styles.receiptValue}>{vending.accountName || t("history.web.notApplicable")}</span>
							</div>
							<div className={styles.detailRow}>
								<span className={styles.receiptLabel}>{t("history.serviceAddress")}</span>
								<span className={styles.receiptValue}>{vending.address || t("history.web.notApplicable")}</span>
							</div>
							<div className={styles.detailRow}>
								<span className={styles.receiptLabel}>{t("history.purchaseType")}</span>
								<span className={styles.receiptValue}>{t("common.labels.prepaid")}</span>
							</div>

							{hasVendingToken && (
								<>
									<div className={styles.detailRow}>
										<span className={styles.receiptLabel}>{t("history.unitsPurchased")}</span>
										<span className={styles.receiptValue}>{vending.vendingToken?.totalKwh != null ? `${vending.vendingToken.totalKwh} kWh` : t("history.web.notApplicable")}</span>
									</div>
									<div className={styles.detailRow}>
										<span className={styles.receiptLabel}>{t("history.tokenLabel")}</span>
										<span className={styles.receiptValueToken}>{tokenValue}</span>
									</div>
								</>
							)}
						</>
					)}
					<div className={styles.detailRow}>
						<span className={styles.receiptLabel}>{t("history.costOfUnit")}</span>
						<span className={styles.receiptValue}>
							{transaction.vendingDetails?.amount
								? formatCurrency(transaction.vendingDetails.amount.amount, { currency: transaction.vendingDetails.amount.currency })
								: t("history.web.notApplicable")}
						</span>
					</div>
					<div className={styles.detailRow}>
						<span className={styles.receiptLabel}>{t("common.labels.date")}</span>
						<span className={styles.receiptValue}>
							{transaction.createdAt ? `${formatDate(transaction.createdAt)}, ${formatTime(transaction.createdAt).toLocaleLowerCase()}` : t("history.web.notApplicable")}
						</span>
					</div>
				</div>
			)}

			{/* Reference ID footer */}
			{shareView && (
				<div className={styles.footer}>
					<div className={styles.detailRow}>
						<span className={styles.receiptLabel}>{t("history.transactionIdLabel")}</span>
						<div className={styles.copyContainer}>
							<span className={styles.receiptValue}>{transaction.reference || t("history.web.notApplicable")}</span>
							{transaction.reference && <CopyIcon onClick={() => handleCopy(transaction.reference)} className={styles.copyBtn} />}
						</div>
					</div>
					<div className={styles.detailRow}>
						<span className={styles.receiptLabel}>{t("history.web.requestId")}</span>
						<div className={styles.copyContainer}>
							<span className={styles.receiptValue}>{transaction.externalReference || t("history.web.notApplicable")}</span>
							{transaction.externalReference && <CopyIcon onClick={() => handleCopy(transaction.externalReference)} className={styles.copyBtn} />}
						</div>
					</div>
				</div>
			)}
		</>
	);
};
